import { useCallback } from "react";

import type {
    ActivationAlertsContractSocialMediaChannel,
    ActivationAlertsContractSocialMediaShareResponse,
} from "@similarweb/pro-api-client";
import { colorsSets } from "@similarweb/styles";

import { useApiQuery } from "app/modules/api-client";

import { useActivationSet } from "../../../../hooks/useActivationSet";
import { useActivationSetFavicons } from "../../../../hooks/useActivationSetFavicons";
import type { IChartDataItem } from "../../../common/widget/donut-metric/types";
import { useDomainColorMap } from "../../../common/widget/useDomainColorMap";
import { useCommonAlertsQueryParams } from "../../../useCommonAlertsQueryParams";
import { useSocialAlertsFilterStore } from "../../filters/useSocialAlertsFilterStore";

const colors = colorsSets.c.toArray();

type ShareMetricMethodName =
    | "activationAlertsMetricsSocialMediaShareOfPostsDetail"
    | "activationAlertsMetricsSocialMediaShareOfViewsDetail";

export const useShareMetricQuery = (methodName: ShareMetricMethodName) => {
    const params = useCommonAlertsQueryParams();
    const { activationSet } = useActivationSet();
    const domainColorMap = useDomainColorMap();
    const { favicons } = useActivationSetFavicons();
    const primaryDomain = activationSet?.primaryDomain;
    const selectedChannels = useSocialAlertsFilterStore((state) => state.selectedChannels);
    const isDefaultChannelsSelected = useSocialAlertsFilterStore((state) => state.isDefaultChannelsSelected);
    const socialNetworks = isDefaultChannelsSelected
        ? undefined
        : ([...selectedChannels.keys()] as ActivationAlertsContractSocialMediaChannel[]);
    const select = useCallback(
        (data: ActivationAlertsContractSocialMediaShareResponse[]): { data: IChartDataItem[] }[] | null => {
            if (!data?.length) {
                return null;
            }

            const shares = data.map((item) => item.share);
            const maxShare = Math.max(...shares);

            return [
                {
                    data: data.map((item, index) => {
                        const share = item.share;
                        const isPrimary = item.domain === primaryDomain;
                        const isMaxValue = share === maxShare;

                        return {
                            id: item.domain,
                            color: domainColorMap?.[item.domain] ?? colors[index % colors.length],
                            name: item.domain,
                            visible: true,
                            y: share,
                            value: share,
                            isPrimary,
                            isMaxValue,
                            icon: favicons?.[item.domain] ?? null,
                        } satisfies IChartDataItem;
                    }),
                },
            ];
        },
        [domainColorMap, primaryDomain, favicons],
    );

    return useApiQuery(
        {
            tag: "Alerts",
            methodName,
            options: {
                refetchOnMount: true,
                select,
            },
        },
        { ...params, socialNetworks },
    );
};
